/**
 * Spaced Repetition System (SRS)
 * Based on SM-2 algorithm with simplified intervals
 */

// Intervals in days for each level
const INTERVALS = {
  0: 0,      // New/Failed - show immediately
  1: 1,      // Learning - 1 day
  2: 3,      // Familiar - 3 days
  3: 7,      // Known - 7 days
  4: 14,     // Well Known - 14 days
  5: 30,     // Mastered - 30 days
  6: 90,     // Expert - 90 days
};

/**
 * Get word review data from localStorage
 */
export function getWordReviewData() {
  try {
    const data = localStorage.getItem('langi_word_reviews');
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error loading word reviews:', error);
    return {};
  }
}

/**
 * Save word review data to localStorage
 */
export function saveWordReviewData(data) {
  try {
    localStorage.setItem('langi_word_reviews', JSON.stringify(data));
  } catch (error) {
    console.error('Error saving word reviews:', error);
  }
}

/**
 * Get unique word ID
 */
export function getWordId(word) {
  return `${word.pl}_${word.de}`.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Record a word review
 * @param {Object} word - The word object { pl, de }
 * @param {boolean} correct - Whether the answer was correct
 * @param {number} responseTime - Time taken to answer in seconds
 */
export function recordReview(word, correct, responseTime = 0) {
  const reviews = getWordReviewData();
  const wordId = getWordId(word);
  
  const now = Date.now();
  const existing = reviews[wordId] || {
    wordId,
    word,
    level: 0,
    reviews: 0,
    correct: 0,
    wrong: 0,
    lastReview: null,
    nextReview: now,
    streak: 0,
    averageTime: 0,
    createdAt: now,
  };
  
  // Update statistics
  existing.reviews++;
  existing.lastReview = now;
  
  if (correct) {
    existing.correct++;
    existing.streak++;
    // Move up a level (max 6)
    existing.level = Math.min(existing.level + 1, 6);
  } else {
    existing.wrong++;
    existing.streak = 0;
    // Drop to level 0 (needs immediate review)
    existing.level = 0;
  }
  
  // Update average response time
  if (responseTime > 0) {
    existing.averageTime = existing.averageTime === 0
      ? responseTime
      : (existing.averageTime + responseTime) / 2;
  }
  
  // Calculate next review date
  const intervalDays = INTERVALS[existing.level];
  existing.nextReview = now + (intervalDays * 24 * 60 * 60 * 1000);
  
  reviews[wordId] = existing;
  saveWordReviewData(reviews);
  
  return existing;
}

/**
 * Get words that need review
 * @param {Array} words - Array of word objects
 * @returns {Array} Words sorted by priority (most urgent first)
 */
export function getWordsForReview(words) {
  const reviews = getWordReviewData();
  const now = Date.now();
  
  return words
    .map(word => {
      const wordId = getWordId(word);
      const review = reviews[wordId];
      
      if (!review) {
        // New word - highest priority
        return { word, priority: 1000, review: null };
      }
      
      // Calculate priority based on:
      // 1. Overdue time
      // 2. Level (lower level = higher priority)
      // 3. Wrong answer count
      
      const overdueDays = (now - review.nextReview) / (24 * 60 * 60 * 1000);
      const levelPriority = (6 - review.level) * 10;
      const errorPriority = review.wrong * 5;
      
      const priority = Math.max(0, overdueDays) + levelPriority + errorPriority;
      
      return { word, priority, review };
    })
    .sort((a, b) => b.priority - a.priority)
    .map(item => item.word);
}

/**
 * Get mastery level for a word
 * @param {Object} word
 * @returns {Object} { level, label, color, percentage }
 */
export function getWordMastery(word) {
  const reviews = getWordReviewData();
  const wordId = getWordId(word);
  const review = reviews[wordId];
  
  if (!review) {
    return { level: 0, label: 'New', color: '#94A3B8', percentage: 0 };
  }
  
  const levels = [
    { level: 0, label: 'Learning', color: '#EF4444', percentage: 0 },
    { level: 1, label: 'Beginner', color: '#F97316', percentage: 16 },
    { level: 2, label: 'Familiar', color: '#F59E0B', percentage: 33 },
    { level: 3, label: 'Known', color: '#EAB308', percentage: 50 },
    { level: 4, label: 'Well Known', color: '#84CC16', percentage: 66 },
    { level: 5, label: 'Mastered', color: '#22C55E', percentage: 83 },
    { level: 6, label: 'Expert', color: '#10B981', percentage: 100 },
  ];
  
  return levels[review.level];
}

/**
 * Get set mastery statistics
 * @param {Array} words - Array of words in the set
 * @returns {Object} Statistics about the set
 */
export function getSetMastery(words) {
  const reviews = getWordReviewData();
  
  let totalLevel = 0;
  let reviewedCount = 0;
  let masteredCount = 0;
  
  words.forEach(word => {
    const wordId = getWordId(word);
    const review = reviews[wordId];
    
    if (review) {
      totalLevel += review.level;
      reviewedCount++;
      if (review.level >= 5) masteredCount++;
    }
  });
  
  const averageLevel = reviewedCount > 0 ? totalLevel / reviewedCount : 0;
  const percentage = (reviewedCount / words.length) * 100;
  const masteryPercentage = (masteredCount / words.length) * 100;
  
  return {
    total: words.length,
    reviewed: reviewedCount,
    new: words.length - reviewedCount,
    mastered: masteredCount,
    averageLevel: averageLevel.toFixed(1),
    percentage: percentage.toFixed(0),
    masteryPercentage: masteryPercentage.toFixed(0),
  };
}

/**
 * Get words due for review today
 * @param {Array} words
 * @returns {Array} Words that should be reviewed today
 */
export function getDueWords(words) {
  const reviews = getWordReviewData();
  const now = Date.now();
  
  return words.filter(word => {
    const wordId = getWordId(word);
    const review = reviews[wordId];
    
    // New words are always due
    if (!review) return true;
    
    // Check if next review date has passed
    return review.nextReview <= now;
  });
}

/**
 * Reset word progress (for testing or starting over)
 */
export function resetWordProgress(word) {
  const reviews = getWordReviewData();
  const wordId = getWordId(word);
  delete reviews[wordId];
  saveWordReviewData(reviews);
}

/**
 * Reset all progress
 */
export function resetAllProgress() {
  localStorage.removeItem('langi_word_reviews');
}
